'use client';

import Image from "next/image";
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useState, useEffect, useRef, Suspense } from 'react';
import dynamic from 'next/dynamic'; // Import dynamic
import { getNadchodzace, getCourses } from '@/lib/api';
import { FaClock, FaStar, FaCalendarAlt, FaArrowRight, FaInfoCircle, FaGraduationCap, FaAnchor, FaSearch, FaTable, FaCalendarDay, FaArrowLeft, FaExclamationTriangle, FaChevronDown, FaIdCard, FaArrowDown } from 'react-icons/fa';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { formatKodIMO } from '@/lib/utils';
import { useLoading } from '@/contexts/LoadingContext';

// Komponent zastępczy dla ładowania
const ComponentLoading = () => (
  <div className="h-64 flex justify-center items-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(6,40,95)]"></div>
  </div>
);

// Dynamiczne ładowanie komponentów
const SimpleCalendar = dynamic(() => import('@/components/SimpleCalendar'), { 
  loading: () => <ComponentLoading />,
  ssr: false
});

// ssr:true — prawdziwe opinie klientów renderują się serwerowo (czytelne dla Google/LLM jako tekst).
const GoogleReviews = dynamic(() => import('@/components/GoogleReviews'), {
  loading: () => <ComponentLoading />,
  ssr: true
});

const SocialMediaSection = dynamic(() => import('@/components/SocialMediaSection'), {
  loading: () => <ComponentLoading />,
  ssr: false,
});

// Leniwe ładowanie wideo. loading:null — DOPÓKI chunk się nie załaduje, widoczny jest
// serwerowy poster (next/image poniżej). Żaden placeholder nie może zasłaniać elementu LCP.
const LazyVideo = dynamic(() => import('@/components/LazyVideo'), {
  loading: () => null,
  ssr: false
});

// Leniwie ładowane komponenty sekcji
// ssr:true — kafelki „Nadchodzące Szkolenia" mają renderować się serwerowo (widoczne dla Google/LLM).
const UpcomingCoursesSection = dynamic(() => import('@/components/UpcomingCoursesSection'), {
  loading: () => <ComponentLoading />,
  ssr: true
});

// ssr:true — tabela kursów to jedyne miejsce na stronie głównej z linkami do stron kursów.
// Przy ssr:false w surowym HTML nie było ANI JEDNEGO linku /training/*, więc dla Google
// i botów AI strona główna nie prowadziła do oferty. Dane przychodzą propsami z page.tsx,
// a window/sessionStorage używane są wyłącznie w handlerach i useEffect → SSR bezpieczny.
const CoursesTableSection = dynamic(() => import('@/components/CoursesTableSection'), {
  loading: () => <ComponentLoading />,
  ssr: true
});

// Interfejs dla nadchodzącego kursu (płaska struktura z MySQL)
interface UpcomingCourse {
  id: number;
  tytul: string;
  datarozpoczecia?: string;
  datazakonczenia?: string;
  godzinarozpoczecia?: string;
  kodimo?: number;
  cena?: string;
  idszkolenie?: number; // Nazwa kolumny w bazie
  motorowodne?: number;
  level?: string;
  miejsce?: boolean | number | string;
  obraz_kursu?: string;
}

// Usunięto tablicę z obrazami, ponieważ wyświetlamy tylko film

// Typ dnia z kursami
interface CalendarDay {
  date: Date;
  courses: UpcomingCourse[];
  isCurrentMonth: boolean;
  isToday: boolean;
  position?: { top: number; left: number };
}

export default function HomeClient({
  initialUpcoming = [],
  initialAllCourses = [],
}: {
  initialUpcoming?: UpcomingCourse[];
  initialAllCourses?: any[];
}) {
  // Seed z danych serwerowych → kafelki „Nadchodzące Szkolenia" renderują się od razu (SSR), bez pustego stanu.
  const [upcomingCourses, setUpcomingCourses] = useState<UpcomingCourse[]>(initialUpcoming);
  const { stopLoading, showLoadingScreen, setLoadingWithDelay } = useLoading();
  const [loading, setLoading] = useState(initialUpcoming.length === 0);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Usunięto zmienną stanu dla zmiany obrazów, ponieważ wyświetlamy tylko film
  const [viewMode, setViewMode] = useState<'cards' | 'table' | 'calendar'>('table');
  // Seed z serwera — tabela kursów (widok domyślny) renderuje się z pełną listą już w SSR,
  // dzięki czemu linki do stron kursów są w surowym HTML. useEffect poniżej i tak odświeży dane.
  const [allCourses, setAllCourses] = useState<any[]>(initialAllCourses);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [dataLoaded, setDataLoaded] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [pageFullyLoaded, setPageFullyLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Funkcja do formatowania daty
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return '';
    }
  };

  // Funkcja do formatowania nazwy miesiąca
  const getMonthName = (monthIndex: number) => {
    return new Date(2024, monthIndex).toLocaleString('pl-PL', { month: 'long' });
  };

  // Funkcja sprawdzająca czy data jest z wybranego miesiąca
  const isDateInSelectedMonth = (dateString?: string) => {
    if (!dateString) return false;
    try {
      const date = new Date(dateString);
      return date.getMonth() === selectedMonth;
    } catch (error) {
      return false;
    }
  };

  // Funkcje do nawigacji po sliderze
  const nextSlide = () => {
    if (upcomingCourses.length <= 3) return;
    
    const now = new Date().getTime();
    let nextIndex = currentIndex + 3;
    
    // Sprawdź czy są jeszcze przyszłe kursy
    const hasMoreUpcoming = upcomingCourses.slice(nextIndex).some(course => 
      course.datarozpoczecia && new Date(course.datarozpoczecia).getTime() > now
    );
    
    if (hasMoreUpcoming) {
      setCurrentIndex(nextIndex);
    }
  };

  const prevSlide = () => {
    if (upcomingCourses.length <= 3) return;
    
    const prevIndex = currentIndex - 3;
    if (prevIndex >= 0) {
      setCurrentIndex(prevIndex);
    }
  };

  // Funkcja sprawdzająca czy kurs jest przeszły
  const isPastCourse = (course: UpcomingCourse) => {
    if (!course.datarozpoczecia) return false;
    return new Date(course.datarozpoczecia) < new Date();
  };

  // Funkcja sprawdzająca czy kurs jest przyszły
  const isFutureCourse = (course: UpcomingCourse) => {
    if (!course.datarozpoczecia) return false;
    return new Date(course.datarozpoczecia) > new Date();
  };

  // Funkcja do pobierania obrazka kursu
  const getCourseImage = (course: UpcomingCourse) => {
    try {
      // Sprawdź czy kurs ma bezpośrednio przypisany obraz
      if (course.obraz_kursu && course.obraz_kursu !== '') {
        return `/images/${course.obraz_kursu}`;
      }
      
      // Jeśli nie ma przypisanego obrazu, użyj domyślnego
      return '/images/course-default.webp';
    } catch (error) {
      return '/images/course-default.webp';
    }
  };

  // Efekt, który przewija stronę na górę tylko raz przy wejściu na stronę główną
  useEffect(() => {
    window.scrollTo(0, 0);

    try {
      sessionStorage.removeItem('scrollPosition');
      sessionStorage.removeItem('lastVisitedSection');
      sessionStorage.removeItem('navigatingFromEvent');
      sessionStorage.removeItem('navigatingTime');
      sessionStorage.removeItem('forceScrollTop');
      sessionStorage.removeItem('scrollBeforeViewChange');
      sessionStorage.removeItem('scrollBeforeMonthChange');
    } catch (e) {
      // Ignoruj błędy dostępu do sessionStorage
    }
  }, []);

  // Single loading effect - stop loading screen shortly after mount
  useEffect(() => {
    // Clean up navigation flags
    try {
      sessionStorage.removeItem('navigatingFromEvent');
      sessionStorage.removeItem('navigatingTime');
    } catch (e) {
      // ignore
    }

    // Stop loading screen after a short delay
    const timer = setTimeout(() => {
      stopLoading();
      setPageFullyLoaded(true);
    }, 300);

    // Safety timeout - max 5s
    const safetyTimer = setTimeout(() => {
      stopLoading();
      showLoadingScreen(false);
      setPageFullyLoaded(true);
    }, 5000);

    return () => {
      clearTimeout(timer);
      clearTimeout(safetyTimer);
    };
  }, []);

  // Usunięto efekt zmiany slajdów, ponieważ wyświetlamy tylko film

  // Efekt zmiany trybu wyświetlania na urządzeniach mobilnych
  useEffect(() => {
    const handleResize = () => {
      // Usuwam automatyczne przełączanie widoku na urządzeniach mobilnych
      // Zachowujemy aktualny widok wybrany przez użytkownika
      if (window.innerWidth < 768) {
        // Tylko sprawdzamy czy urządzenie jest mobilne, ale nie zmieniamy widoku
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };

    // Sprawdź przy pierwszym załadowaniu
    handleResize();

    // Nasłuchuj zmiany rozmiaru okna
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Removed: 4 competing timeout effects (data loaded check, 15s safety, error handler, video loaded)
  // Loading is now handled by the single loading effect above + LoadingContext safety timeout

  // Pobieranie nadchodzących kursów przy ładowaniu strony
  useEffect(() => {
    const controller = new AbortController();
    let timeoutId: NodeJS.Timeout;
    
    async function loadUpcomingCourses() {
      if (upcomingCourses.length > 0) {
        // Dane już są załadowane, nie pobieraj ponownie
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Timeout dla API call - maksymalnie 8 sekund
        timeoutId = setTimeout(() => {
          setLoading(false);
          setUpcomingCourses([]);
        }, 8000);
        
        let courses;
        
        try {
          courses = await getNadchodzace();
          clearTimeout(timeoutId);
        } catch (error) {
          courses = [];
          clearTimeout(timeoutId);
        }
        
        // Sortuj kursy według daty rozpoczęcia
        const sortedCourses = [...courses].sort((a, b) => {
          const dateA = a.datarozpoczecia ? new Date(a.datarozpoczecia).getTime() : Infinity;
          const dateB = b.datarozpoczecia ? new Date(b.datarozpoczecia).getTime() : Infinity;
          return dateA - dateB;
        });
        
        setUpcomingCourses(sortedCourses);
        setDataLoaded(true);
        
        // Ustaw początkowy indeks na pierwszy nadchodzący kurs
        const now = new Date().getTime();
        const firstUpcomingIndex = sortedCourses.findIndex(course => 
          course.datarozpoczecia && new Date(course.datarozpoczecia).getTime() > now
        );
        setCurrentIndex(Math.max(0, firstUpcomingIndex - (firstUpcomingIndex % 3)));
      } catch (error) {
        setUpcomingCourses([]);
      } finally {
        setLoading(false);
        if (timeoutId) clearTimeout(timeoutId);
      }
    }

    loadUpcomingCourses();
    
    return () => {
      controller.abort();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [upcomingCourses.length]); // Dodaję dependency aby uniknąć re-render loops

  // Pobieranie wszystkich kursów dla widoku tabeli
  useEffect(() => {
    const loadAllCourses = async () => {
      try {
        const response = await getCourses();
        
        if (response) {
          // Sortowanie kursów
          const sortedCourses = response.sort((a, b) => {
            // Najpierw sprawdź kolejnosc_home - jeśli jest zdefiniowana, użyj jej jako głównego kryterium sortowania
            const kolejnoscA = typeof a.kolejnosc_home === 'number' ? a.kolejnosc_home : 999;
            const kolejnoscB = typeof b.kolejnosc_home === 'number' ? b.kolejnosc_home : 999;

            // Zawsze sortujemy najpierw według kolejności (niezależnie od wartości)
            return kolejnoscA - kolejnoscB;
          });

          setAllCourses(sortedCourses);
        }
      } catch (error) {
        // Obsługa błędu pobierania danych - nie blokuj strony
      }
    };

    loadAllCourses();
  }, []);

  // Sprawdzanie hash URL i przełączanie na kalendarz
  useEffect(() => {
    // Sprawdź, czy jest parametr view w URL i ustaw odpowiedni widok
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    
    // Sprawdź czy jest flaga przekierowania z navbara
    const redirectedFromNavbar = sessionStorage.getItem('redirectedFromNavbar');
    
    if (viewParam === 'calendar' || redirectedFromNavbar === 'true') {
      setViewMode('calendar');
      // Zapisz preferencję użytkownika
      sessionStorage.setItem('homeViewMode', 'calendar');
      
      // Usuń flagę przekierowania - wykonuje się tylko raz po przeładowaniu strony
      if (redirectedFromNavbar === 'true') {
        sessionStorage.removeItem('redirectedFromNavbar');
        
        // Scroll do sekcji kursów po krótkim opóźnieniu (aby strona zdążyła się załadować)
        setTimeout(() => {
          const viewSwitcherElement = document.getElementById('view-switcher');
          if (viewSwitcherElement) {
            // Oblicz pozycję z offsetem, aby przyciski były w pełni widoczne
            const scrollOffset = -120; // Zwiększony offset w pikselach
            const elementPosition = viewSwitcherElement.getBoundingClientRect().top + window.pageYOffset;
            
            // Przewiń do przełącznika widoku z offsetem
            window.scrollTo({
              top: elementPosition + scrollOffset,
              behavior: 'smooth'
            });
          }
        }, 500);
      }
    } else {
      // Pobierz zapisany tryb widoku lub użyj domyślnego
      const savedViewMode = sessionStorage.getItem('homeViewMode');
      if (savedViewMode && ['cards', 'table', 'calendar'].includes(savedViewMode)) {
        setViewMode(savedViewMode as 'cards' | 'table' | 'calendar');
      }
    }
    
    // Obsługa zdarzenia zmiany widoku wywoływanego z navbara
    const handleViewModeChange = (event: CustomEvent) => {
      if (event.detail.mode === 'calendar' || event.detail.mode === 'table') {
        // Najpierw zmień widok
        setViewMode(event.detail.mode);
        
        // Zapisz preferencję użytkownika
        sessionStorage.setItem('homeViewMode', event.detail.mode);
        
        // Jeśli w evencie jest flaga scrollTo, wykonaj przewijanie
        if (event.detail.scrollTo) {
          // Użyj setTimeout z krótkim opóźnieniem, aby dać czas na renderowanie widoku
        setTimeout(() => {
            // Przewiń do przełącznika widoku zamiast do całej sekcji kursów
            const viewSwitcherElement = document.getElementById('view-switcher');
            if (viewSwitcherElement) {
              // Oblicz pozycję z offsetem, aby przyciski były w pełni widoczne
              const scrollOffset = -120; // Zwiększony offset w pikselach
              const elementPosition = viewSwitcherElement.getBoundingClientRect().top + window.pageYOffset;
              
              // Przewiń do przełącznika widoku z offsetem
          window.scrollTo({
                top: elementPosition + scrollOffset,
                behavior: 'smooth'
          });
            }
        }, 100);
        }
      }
    };
    
    window.addEventListener('setViewMode', handleViewModeChange as EventListener);
    
    return () => {
      window.removeEventListener('setViewMode', handleViewModeChange as EventListener);
    };
  }, []);

  // Efekt do obsługi zmiany widoku bez przewijania
  useEffect(() => {
    // Zapisz aktualny tryb widoku
    sessionStorage.setItem('homeViewMode', viewMode);
    
    // Dodajemy flagę, aby zapobiec automatycznemu przełączaniu widoku na urządzeniach mobilnych
    if (viewMode === 'calendar') {
      document.body.classList.add('calendar-view-active');
      
      // Dodajemy nasłuchiwanie na przewijanie, aby zapobiec automatycznemu przełączaniu widoku
      const handleScroll = () => {
        // Jeśli jesteśmy na urządzeniu mobilnym i widok kalendarza jest aktywny,
        // nie pozwalamy na automatyczne przełączanie widoku
        if (document.body.classList.contains('is-mobile-device') && 
            document.body.classList.contains('calendar-view-active')) {
          // Upewniamy się, że widok kalendarza pozostaje aktywny
          const savedViewMode = sessionStorage.getItem('homeViewMode');
          if (savedViewMode !== 'calendar') {
            sessionStorage.setItem('homeViewMode', 'calendar');
          }
        }
      };
      
      window.addEventListener('scroll', handleScroll, { passive: true });
      
      // Obsługa zapisywania stanu przed opuszczeniem strony
      const handleBeforeUnload = () => {
        sessionStorage.setItem('homeViewMode', viewMode);
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    } else {
      document.body.classList.remove('calendar-view-active');
      
      // Obsługa zapisywania stanu przed opuszczeniem strony
      const handleBeforeUnload = () => {
        sessionStorage.setItem('homeViewMode', viewMode);
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [viewMode]);

  // Removed: duplicate resize listener for isMobile - already handled above

  // Efekt sprawdzający czy mamy przewinąć do kalendarza po załadowaniu strony
  useEffect(() => {
    let scrollTimer: NodeJS.Timeout;
    // Sprawdź, czy mamy przewinąć do kalendarza po załadowaniu strony
    const shouldScrollToCalendar = sessionStorage.getItem('scrollToCalendarAfterLoad') === 'true';

    if (shouldScrollToCalendar && pageFullyLoaded) {
      // Usuń flagę, aby nie przewijać ponownie przy kolejnych odświeżeniach
      sessionStorage.removeItem('scrollToCalendarAfterLoad');

      // Upewnij się, że widok kalendarza jest aktywny
      setViewMode('calendar');
      sessionStorage.setItem('homeViewMode', 'calendar');

      // Daj więcej czasu na pełne załadowanie strony i dopiero wtedy przewiń
      scrollTimer = setTimeout(() => {
        const viewSwitcherElement = document.getElementById('view-switcher');
        if (viewSwitcherElement) {
          const scrollOffset = -120;
          const elementPosition = viewSwitcherElement.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition + scrollOffset,
            behavior: 'smooth'
          });
        }
      }, 800);
    }

    return () => {
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [pageFullyLoaded]);

  // Funkcja do płynnego przewijania do harmonogramu kursów z offsetem
  const scrollToCoursesTable = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Ustaw widok tabeli
    setViewMode('table');
    sessionStorage.setItem('homeViewMode', 'table');
    
    // Przewiń do sekcji przełącznika widoku zamiast do całej sekcji kursów
    const viewSwitcherElement = document.getElementById('view-switcher');
    if (viewSwitcherElement) {
      // Oblicz pozycję z offsetem, aby przyciski były w pełni widoczne
      const scrollOffset = -120; // Zwiększony offset w pikselach
      const elementPosition = viewSwitcherElement.getBoundingClientRect().top + window.pageYOffset;
      
      // Przewiń do przełącznika widoku z offsetem
      window.scrollTo({
        top: elementPosition + scrollOffset,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 bg-black">
          {/* Poster hero — ELEMENT LCP renderowany serwerowo (jest w HTML z serwera).
              next/image z priority dokłada <link rel="preload"> i fetchpriority="high",
              a srcset (deviceSizes) dobiera wariant mobilny automatycznie — bez osobnego mobilePoster. */}
          <Image
            src="/images/front-film1-poster.webp"
            alt=""
            fill
            priority
            fetchPriority="high"
            sizes="100vw"
            quality={68}
            className="object-cover scale-[1.01]"
          />
          {/* Wideo — montuje się DOPIERO po LCP i płynnie zastępuje poster (crossfade). */}
          <LazyVideo
            src="/images/front-film1-720.mp4"
            mobileSrc="/images/front-film1-540.mp4"
            className="w-full h-full scale-[1.01]"
            autoPlay={true}
            muted={true}
            loop={true}
            overlayOpacity={0}
            onLoaded={() => {
              setVideoLoaded(true);
            }}
          />
        </div>

        {/* Ciemna nakładka — serwerowa (w HTML z serwera), spójne przyciemnienie postera i wideo
            od pierwszego renderu (dawniej rysowana wewnątrz LazyVideo, po hydratacji). */}
        <div className="absolute inset-0 bg-black/50 pointer-events-none z-[1]"></div>

        {/* Dodanie nakładki z gradientem dla zwiększenia energii */}
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(6,40,95,0.4)] to-transparent pointer-events-none z-[1]"></div>
          
        {/* Animowane cząsteczki */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-6 h-6 rounded-full bg-[rgba(6,40,95,0.5)] animate-pulse opacity-70"></div>
          <div className="absolute top-1/3 right-1/4 w-4 h-4 rounded-full bg-[rgba(6,40,95,0.3)] animate-pulse opacity-50 animation-delay-1000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-8 h-8 rounded-full bg-[rgba(6,40,95,0.2)] animate-pulse opacity-60 animation-delay-2000"></div>
          <div className="absolute top-1/2 right-1/3 w-5 h-5 rounded-full bg-[rgba(6,40,95,0.4)] animate-pulse opacity-40 animation-delay-3000"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
            className="mb-8"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-2 text-shadow-lg relative">
              <span className="inline-block relative">
                Twoja <span className="mr-2">Droga</span>
                <span className="text-[rgb(120,180,255)] relative ml-1 font-bold">
                  Na Morze
                </span>
                <div className="mt-2">
                  <span>Zaczyna Się Tutaj</span>
                </div>
              </span>
            </h1>
          </motion.div>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto drop-shadow-md"
          >
            Szkolenia dla marynarzy według Konwencji STCW
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex justify-center"
          >
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/training"
                className="inline-flex items-center justify-center px-8 py-3 text-base font-medium text-white bg-[rgb(6,40,95)] hover:bg-[rgb(6,40,95)]/90 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg border border-blue-400/30 backdrop-blur-sm"
              >
                <span className="relative z-10">Zobacz nasze kursy</span>
                <div className="absolute top-0 right-0 -mt-1 -mr-1 w-3 h-3 rounded-full bg-blue-400 animate-ping"></div>
              </Link>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" 
                }}
              >
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    
                    // Ustaw widok tabeli
                    setViewMode('table');
                    sessionStorage.setItem('homeViewMode', 'table');
                    
                    // Nawiguj do sekcji z przyciskami bez przewijania
                    const viewSwitcherElement = document.getElementById('view-switcher');
                    if (viewSwitcherElement) {
                      // Oblicz pozycję z offsetem, aby przyciski były w pełni widoczne
                      const scrollOffset = -120; // Zwiększony offset w pikselach
                      const elementPosition = viewSwitcherElement.getBoundingClientRect().top + window.pageYOffset;
                      
                      // Przewiń do przełącznika widoku z offsetem - używamy smooth dla lepszego UX
                      window.scrollTo({
                        top: elementPosition + scrollOffset,
                        behavior: 'smooth'
                      });
                    }
                  }}
                  className="inline-flex items-center justify-center px-8 py-3 text-base font-medium text-[rgb(6,40,95)] bg-white hover:bg-blue-50 rounded-lg transition-all duration-300 group"
                >
                  <span>Harmonogram kursów</span>
                  <div className="ml-2 animate-bounce">
                    <FaArrowDown className="h-5 w-5" />
                  </div>
                </button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Courses Section */}
      <section id="courses-table" className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4 relative inline-block">
              Nadchodzące Szkolenia
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-[rgb(6,40,95)] transform scale-x-0 transition-transform duration-500 group-hover:scale-x-100"></div>
            </h2>
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Zaplanuj swoje szkolenie już dziś i zarezerwuj miejsce na jednym z naszych kursów
            </motion.p>
          </motion.div>

          {/* Kafelki „Nadchodzące Szkolenia" — renderowane serwerowo (ssr:true), dane z page.tsx przez propsy */}
          <UpcomingCoursesSection initialUpcomingCourses={upcomingCourses} initialAllCourses={allCourses} />
          
          {/* Przyciski przełączania widoku i widok kalendarza/tabeli */}
          {!loading && upcomingCourses.length > 0 && (
            <>
              <motion.div 
                id="view-switcher"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex justify-center mt-16 mb-8 pt-10 md:pt-12"
              >
                <div className="inline-flex bg-gray-100 rounded-lg p-1.5 shadow-md">
                  <motion.button
                    data-view-mode="table"
                    onClick={(e) => {
                      e.preventDefault();
                      
                      // Ustaw widok tabeli
                      setViewMode('table');
                      sessionStorage.setItem('homeViewMode', 'table');
                      
                      // Nawiguj do sekcji z przyciskami bez przewijania
                      const viewSwitcherElement = document.getElementById('view-switcher');
                      if (viewSwitcherElement) {
                      // Dodaj fragment do URL bez przewijania
                      if (history.pushState) {
                        history.pushState(null, '', '#view-switcher');
                        }
                        
                        // Oblicz pozycję z offsetem, aby przyciski były w pełni widoczne
                        const scrollOffset = -120; // Zwiększony offset w pikselach
                        const elementPosition = viewSwitcherElement.getBoundingClientRect().top + window.pageYOffset;
                        
                        // Przewiń do przełącznika widoku z offsetem
                        window.scrollTo({
                          top: elementPosition + scrollOffset,
                          behavior: 'smooth'
                        });
                      }
                    }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all duration-300 ${
                      viewMode === 'table' 
                        ? 'bg-[rgb(6,40,95)] text-white shadow-md' 
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Harmonogram kursów</span>
                    </div>
                  </motion.button>
                  <motion.button
                    data-view-mode="calendar"
                    onClick={(e) => {
                      e.preventDefault();
                      
                      // Ustaw tryb widoku
                      setViewMode('calendar');
                      
                      // Zapisz preferencję użytkownika
                      sessionStorage.setItem('homeViewMode', 'calendar');
                      
                      // Przewiń do przełącznika widoku z offsetem
                      const viewSwitcherElement = document.getElementById('view-switcher');
                      if (viewSwitcherElement) {
                        // Oblicz pozycję z offsetem, aby przyciski były w pełni widoczne
                        const scrollOffset = -120; // Zwiększony offset w pikselach
                        const elementPosition = viewSwitcherElement.getBoundingClientRect().top + window.pageYOffset;
                        
                        // Przewiń do przełącznika widoku z offsetem
                        window.scrollTo({
                          top: elementPosition + scrollOffset,
                          behavior: 'smooth'
                        });
                      }
                    }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all duration-300 ${
                      viewMode === 'calendar' 
                        ? 'bg-[rgb(6,40,95)] text-white shadow-md' 
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Kalendarz szkoleń</span>
                    </div>
                  </motion.button>
                </div>
              </motion.div>

              <div className="mt-8">
                {viewMode === 'calendar' ? (
                  <>
                    <motion.h3 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="text-2xl font-bold text-center text-gray-900 mb-8 relative inline-block"
                    >
                      Kalendarz nadchodzących szkoleń
                      <motion.div 
                        className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[rgb(6,40,95)]"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                      />
                    </motion.h3>
                    {upcomingCourses.length > 0 ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="transform hover:scale-[1.01] transition-transform duration-300"
                      >
                        <SimpleCalendar 
                          courses={isMobile ? upcomingCourses : upcomingCourses} 
                          formatDateFn={formatDate} 
                        />
                      </motion.div>
                    ) : (
                      <div className="bg-white p-8 rounded-xl shadow text-center">
                        <p className="text-lg text-gray-600">Brak nadchodzących kursów do wyświetlenia w kalendarzu.</p>
                      </div>
                    )}
                  </>
                ) : (
                  <CoursesTableSection initialAllCourses={allCourses} initialUpcomingCourses={upcomingCourses} />
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="text-3xl font-bold text-center text-gray-900 mb-16 relative inline-block group"
          >
            <span className="relative z-10">Co mówią nasi kursanci</span>
            <motion.div 
              className="absolute -bottom-2 left-0 right-0 h-1 bg-[rgb(6,40,95)]"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.5 }}
            />
          </motion.h2>
          
          {/* Sekcja opinii z Google Maps */}
          <GoogleReviews />
        </div>
      </section>

      {/* Sekcja Praca na morzu */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <motion.div 
              className="w-full md:w-2/5"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
            >
              <div className="relative">
                <div className="absolute -top-3 -left-3 w-20 h-20 border-t-2 border-l-2 border-[rgb(6,40,95)] rounded-tl-lg"></div>
                <div className="absolute -bottom-3 -right-3 w-20 h-20 border-b-2 border-r-2 border-[rgb(6,40,95)] rounded-br-lg"></div>
                <Image
                  src="/images/pracanamorzu.webp"
                  alt="Ośrodek Szkolenia Morskiego LIBRA"
                  width={400}
                  height={300}
                  className="rounded-lg shadow-lg w-full h-auto object-cover"
                />
              </div>
            </motion.div>
            <motion.div 
              className="w-full md:w-3/5"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-3">O naszym ośrodku</h2>
              <p className="text-base text-gray-600 mb-4">
                Ośrodek Szkolenia Morskiego LIBRA to miejsce, gdzie pasja do morza łączy się z profesjonalizmem.
                Oferujemy szkolenia i certyfikacje uznawane na całym świecie, prowadzone przez doświadczonych instruktorów.
                Nasza misja to kształcenie wykwalifikowanych kadr morskich według najwyższych standardów.
              </p>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block"
              >
                <Link href="/about" className="inline-flex items-center bg-[rgb(6,40,95)] hover:bg-[rgb(10,60,120)] text-white px-5 py-2 rounded-lg font-medium transition-all text-sm">
                  <span>Poznaj nasz ośrodek</span>
                  <FaArrowRight className="ml-2" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sekcja Social Media ładowana dynamicznie */}
      <SocialMediaSection />
    </div>
  );
}

// Komponent do animowanego licznika
const Counter = ({ end, duration, decimal = false }: { end: number, duration: number, decimal?: boolean }) => {
  const [count, setCount] = useState(0);
  const counterRef = useRef(null);
  
  useEffect(() => {
    let startTimestamp: number;
    let animationFrameId: number;
    
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / (duration * 1000), 1);
      
      // Płynna animacja z efektem ease-out
      const easedProgress = 1 - Math.pow(1 - progress, 3); // efekt cubic ease-out
      
      if (decimal) {
        setCount(parseFloat((easedProgress * end).toFixed(1)));
      } else {
        setCount(Math.floor(easedProgress * end));
      }
      
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(step);
      }
    };
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          animationFrameId = requestAnimationFrame(step);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    if (counterRef.current) {
      observer.observe(counterRef.current);
    }
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      observer.disconnect();
    };
  }, [end, duration, decimal]);
  
  return <span ref={counterRef}>{decimal ? count.toFixed(1) : count}</span>;
};
